import * as React from 'react';
import { NavLink } from 'react-router-dom';
import { Auth } from '../../services';
import { FilterStore, UserStore } from '../../stores';
import { UserPermissions } from '../../models';
import { Roles } from '../../stores/Constants';
import { inject, observer } from 'mobx-react';
import { computed } from 'mobx';
import { RouteComponentProps, withRouter } from 'react-router';
import { Ref } from '../../utilities/Ref';

interface NavigationItem {
    isHeader?: boolean;
    isSubHeader?: boolean;
    show?: boolean;
    title?: string;
    route?: string;
    icon?: string;
    allowedRoles: string[];
    allowedPermissions?: UserPermissions[];
    requiredPermissions?: UserPermissions[];
    forbiddenPermissions?: UserPermissions[];
    items: NavigationItem[];
    keyLookUp?: string;
    hideCheck?: boolean;
}

const superAdmins = [Roles.SuperAdmin];
const siteAdmins = superAdmins.concat([Roles.SiteAdmin]);
const associates = siteAdmins.concat([Roles.PonderaAssociate]);
const organizationAdmins = associates.concat([Roles.OrgAdmin]);
const managers = organizationAdmins.concat([Roles.Manager]);
const users = managers.concat([Roles.User]);
const superSiteOrg = [Roles.SuperAdmin, Roles.SiteAdmin, Roles.OrgAdmin];

const navItems: NavigationItem[] = [
    {
        route: '/pvastyles',
        title: 'The 8 PVA Styles',
        icon: '/images/pva styles.svg',
        allowedRoles: users,
        items: []
    },
    {
        isHeader: true,
        show: true,
        title: 'My Reports',
        allowedRoles: users,
        items: [
            {
                route: '/myreport',
                keyLookUp: 'myreport',
                title: 'My PVA Individual Report',
                icon: '/images/pva individual report.svg',
                allowedRoles: users,
                items: []
            },
            {
                route: '/myinteractionreport',
                keyLookUp: 'myinteractionreport',
                title: 'My PVA Interaction Report',
                icon: '/images/pva styles.svg',
                allowedRoles: users,
                forbiddenPermissions: [UserPermissions.Foundations],
                items: []
            }
        ]
    },
    {
        isHeader: true,
        show: true,
        title: 'Team Reports',
        allowedRoles: managers,
        items: [
            {
                route: '/individualreport',
                keyLookUp: 'individualReport',
                title: 'PVA Individual Report',
                icon: '/images/pva styles.svg',
                allowedRoles: managers,
                items: []
            },
            {
                route: '/comparisonreport',
                keyLookUp: 'comparisonReport',
                title: 'PVA Comparison',
                icon: '/images/pva comparison.svg',
                allowedRoles: managers,
                items: []
            },
            {
                route: '/interactionreport',
                keyLookUp: 'interactionReport',
                title: 'PVA Interaction Report',
                icon: '/images/interaction report.svg',
                allowedRoles: managers,
                items: []
            },
            {
                route: '/teamologyreport',
                keyLookUp: 'teamologyReport',
                title: 'PVA Teamology',
                icon: '/images/teamology.svg',
                allowedRoles: managers,
                allowedPermissions: [UserPermissions.Teamology],
                items: []
            }
        ]
    },
    {
        isHeader: true,
        show: true,
        title: 'Old Team Reports',
        allowedRoles: managers,
        items: [
            {
                route: '/scorecard',
                keyLookUp: 'scoreCard',
                title: 'PVA Score Card',
                icon: 'fas fa-award',
                allowedRoles: siteAdmins,
                allowedPermissions: [UserPermissions.ViewFusionScore],
                items: [],
                hideCheck: true
            }
        ]
    },
    {
        isHeader: true,
        show: false,
        title: 'Administrative',
        allowedRoles: siteAdmins,
        items: [
            {
                isHeader: true,
                isSubHeader: true,
                show: false,
                title: 'Configuration Settings',
                allowedRoles: users,
                items: [
                    {
                        route: '/tenant',
                        title: 'Tenants',
                        icon: 'fas fa-users',
                        allowedRoles: siteAdmins,
                        items: []
                    },
                    {
                        route: '/organization',
                        title: 'Organizations',
                        icon: 'fas fa-building',
                        allowedRoles: siteAdmins,
                        items: []
                    },
                    {
                        route: '/team',
                        title: 'Teams',
                        icon: 'fas fa-sitemap',
                        allowedRoles: organizationAdmins,
                        allowedPermissions: [UserPermissions.ViewTeams, UserPermissions.ManageAllTeams, UserPermissions.CreateTeams],
                        items: []
                    },
                    {
                        route: '/language',
                        title: 'Languages',
                        icon: 'fas fa-language',
                        allowedRoles: siteAdmins,
                        items: []
                    },
                    {
                        route: '/user',
                        title: 'Users',
                        icon: 'glyphicon glyphicon-user',
                        allowedRoles: organizationAdmins,
                        allowedPermissions: [UserPermissions.EditUsers],
                        items: []
                    },
                    {
                        route: '/userscore',
                        title: 'User Scores',
                        icon: 'fas fa-chart-line',
                        allowedRoles: superAdmins,
                        allowedPermissions: [UserPermissions.EditScores],
                        requiredPermissions: [UserPermissions.EditScores],
                        items: []
                    },
                    {
                        route: '/scoreComparison',
                        title: 'Score Comparison',
                        icon: 'fas fa-chart-area',
                        allowedRoles: superAdmins,
                        allowedPermissions: [UserPermissions.EditScores],
                        requiredPermissions: [UserPermissions.EditScores],
                        items: []
                    },
                    {
                        route: '/survey',
                        title: 'Surveys',
                        icon: 'fas fa-book',
                        allowedRoles: siteAdmins,
                        items: []
                    },
                    {
                        route: '/surveyinvite',
                        title: 'Survey Invites',
                        icon: 'fas fa-envelope-open',
                        allowedRoles: superSiteOrg,
                        allowedPermissions: [UserPermissions.SendAssessments],
                        items: []
                    },
                    {
                        route: '/sitesettings',
                        title: 'Site Settings',
                        icon: 'fas fa-anchor',
                        allowedRoles: superAdmins,
                        items: []
                    }
                ]
            },
            {
                isHeader: true,
                isSubHeader: true,
                show: false,
                title: 'Report Settings',
                allowedRoles: siteAdmins,
                items: [
                    {
                        route: '/responses',
                        title: 'Responses',
                        icon: 'fas fa-pencil-alt',
                        allowedRoles: siteAdmins,
                        items: []
                    },
                    {
                        route: '/9boxsettings',
                        title: '9 Box Settings',
                        icon: 'glyphicon glyphicon-th',
                        allowedRoles: siteAdmins,
                        items: []
                    },
                    {
                        route: '/teamologysettings',
                        title: 'Teamology Settings',
                        icon: 'glyphicon glyphicon-th',
                        allowedRoles: siteAdmins,
                        items: []
                    },
                    {
                        route: '/scorecards',
                        title: 'Score Card Settings',
                        icon: 'fas fa-award',
                        allowedRoles: siteAdmins,
                        items: []
                    }
                ]
            },
        ]
    },
    {
        isHeader: true,
        show: false,
        title: 'Other Reports',
        allowedRoles: siteAdmins,
        items: [
            {
                route: '/notes',
                title: 'Exchange Notes',
                icon: 'fas fa-sticky-note',
                allowedRoles: siteAdmins,
                allowedPermissions: [UserPermissions.ExchangeNotes],
                items: []
            },
            {
                route: '/teamreport',
                keyLookUp: 'teamReport',
                title: 'PVA Team Report',
                icon: 'fas fa-sliders-h',
                allowedRoles: siteAdmins,
                allowedPermissions: [UserPermissions.TeamReport],
                items: []
            },
            {
                route: '/teamgrid',
                keyLookUp: 'teamGridReport',
                title: 'PVA Team Grid',
                icon: 'fas fa-th-large',
                allowedRoles: siteAdmins,
                allowedPermissions: [UserPermissions.Grid],
                items: []
            },
            {
                route: '/teamlistviewreport',
                keyLookUp: 'teamListViewReport',
                title: 'PVA Team List View',
                icon: 'glyphicon glyphicon-sort-by-attributes',
                allowedRoles: siteAdmins,
                allowedPermissions: [UserPermissions.ListView],
                items: []
            },
            {
                route: '/teamlistviewrireport',
                keyLookUp: 'teamListViewReportRI',
                title: 'PVA Team List View RI',
                icon: 'glyphicon glyphicon-sort-by-attributes',
                allowedRoles: siteAdmins,
                allowedPermissions: [UserPermissions.ListViewRI],
                items: []
            },
            {
                route: '/9box',
                keyLookUp: '9Box',
                title: 'PVA 9-Box',
                icon: 'glyphicon glyphicon-th',
                allowedRoles: siteAdmins,
                allowedPermissions: [UserPermissions.View9Box],
                items: []
            },
            {
                route: '/rfactor',
                keyLookUp: 'rfactor',
                title: 'R Factor',
                icon: 'fas fa-award',
                allowedRoles: [],
                allowedPermissions: [UserPermissions.RFactorReport],
                items: [],
                hideCheck: true
            },
        ]
    },
];

interface NavProps extends Partial<RouteComponentProps<any>> {
    FilterStore?: FilterStore;
    UserStore?: UserStore;
    Auth?: Auth;
}

@inject('FilterStore', 'UserStore', 'Auth') @observer
class NavItems extends React.Component<NavProps> {
    private indexCounter: number = 0;

    constructor(props: NavProps) {
        super(props);
    }

    @computed get organization() {
        if (this.props.FilterStore && this.props.FilterStore.organization) {
            return this.props.FilterStore.organization;
        }
        return null;
    }

    @computed get surveyVersion() {
        if (this.props.FilterStore && this.props.FilterStore.surveyVersion) {
            return this.props.FilterStore.surveyVersion;
        }
        return null;
    }


    @computed get userPermissions() {
        if (this.props.UserStore && this.props.UserStore.userLoaded && this.props.UserStore.userInfo.userPermissions) {
            return this.props.UserStore!.userInfo.userPermissions;
        }
        return [];
    }

    @computed get userRole() {
        if (this.props.UserStore && this.props.UserStore.userLoaded) {
            return this.props.UserStore!.userInfo.userRole;
        }
        return null;
    }

    private filterForbiddenPermissions(navEntry: NavigationItem) {
        if (!!navEntry.forbiddenPermissions) {
            const allowed = navEntry.forbiddenPermissions.every((forbidden: UserPermissions) => !this.props.UserStore!.checkUserPermission(forbidden));
            return allowed;
        }

        return true;
    }

    private filterRequiredPermissions(navEntry: NavigationItem) {
        if (!navEntry.requiredPermissions || navEntry.requiredPermissions.length === 0) {
            // if there is no required permission array, we need to base whether they can see the menu
            // item off of the allowed roles and allowed permissions.
            const hasAllowedRole = this.props.Auth!.userRoleIn(navEntry.allowedRoles);
            const hasAllowedPermission = navEntry.allowedPermissions == null ? false : navEntry.allowedPermissions.some((allowedPermission: UserPermissions) => this.props.UserStore!.checkUserPermission(allowedPermission));
            
            return hasAllowedRole || hasAllowedPermission;
        }

        if (navEntry.requiredPermissions.length > 0) {
            var allFound = true;
            navEntry.requiredPermissions.map((perm: UserPermissions) => {
                if (!this.props.UserStore!.checkUserPermission(perm)) {
                    allFound = false;
                }
            });
            if (allFound) {
                return true;
            }
        }

        if (this.props.Auth!.userRoleIn([Roles.SiteAdmin, Roles.SuperAdmin])) {
            return true;
        }

        return false;
    }


    private filterItems(subItem: NavigationItem) {
        if (subItem.hideCheck) {
            if (subItem.keyLookUp === 'scoreCard') {
                if (!this.organization) {
                    return false;
                }
                if (this.organization.scoreCardCount === 0) {
                    return false;
                }
            }
        }
        if (subItem.isHeader && this.countItemsUnderHeader(subItem) > 0) {
            return true;
        }
        return (this.filterForbiddenPermissions(subItem) && ((this.filterRequiredPermissions(subItem)) ||
            this.props.Auth!.userRoleIn(subItem.allowedRoles)));
    }

    private willShow(navEntry: NavigationItem) {
        return !navEntry.isHeader || navEntry.items.filter(this.filterItems.bind(this)).length > 0;
    }

    private countItemsUnderHeader = (navEntry: any): number => {
        if (navEntry.isHeader) {
            let count: number = 0;
            navEntry.items.forEach((i: NavigationItem) => {
                count += this.countItemsUnderHeader(i);
            });
            return count;
        }
        if (this.filterItems(navEntry)) {
            return 1;
        }

        return 0;
    }

    private isChildRouteActive(navEntry: NavigationItem): boolean {
        const currentPath = this.props.location && this.props.location.pathname;

        if (currentPath && navEntry.route) {
            const route = navEntry.route;
            if (currentPath === route || currentPath.indexOf(route + '/') === 0) {
                return true;
            }
        }

        if (navEntry.items && navEntry.items.length > 0) {
            return navEntry.items.some(item => this.isChildRouteActive(item));
        }

        return false;
    }

    private handleSubItem = (navEntry: any) => {
        if (navEntry.isHeader) {
            if (this.countItemsUnderHeader(navEntry) < 1) {
                return;
            }

            if (this.isChildRouteActive(navEntry)) {
                navEntry.show = true;
            }

            return (
                <div className={`nav-item-container ${navEntry.show ? 'open' : ''}`}
                     key={`navitem-${this.indexCounter++}`}>
                    <div className="side-nav-header" onClick={(e) => { navEntry.show = !navEntry.show; this.forceUpdate(); }}>
                        <div className="side-nav-header-text font-subheading">
                            {navEntry.title}
                        </div>
                        {/*<div className="side-nav-header-toggle">*/}
                        {/*     <span className={`glyphicon glyphicon-triangle-right ${navEntry.show ? 'rotated' : ''}`} />*/}
                        {/*</div>*/}
                    </div>
                    {
                        !navEntry.isSubHeader && <div key={`navitem-${this.indexCounter++}`} className="separator"></div>
                    }
                    {
                        navEntry.items
                            .filter(this.filterItems.bind(this))
                            .map(this.handleSubItem)
                    }
                </div>
            );
        }

        if (!this.filterItems(navEntry)) {
            return;
        }

        return (
            <li key={`navitem-${this.indexCounter++}`}>
                <NavLink to={navEntry.route!} activeClassName="active">
                    {navEntry.icon && navEntry.icon.indexOf('.svg') > -1 ? <img src={navEntry.icon} className="icon" /> : <span className={navEntry.icon} />} {navEntry.title}
                </NavLink>
            </li>
        );
    }

    public render() {
        let jsxNavItems = navItems
            .filter(this.willShow.bind(this))
            .map(this.handleSubItem);

        // insert a home nav item for everyone
        jsxNavItems.unshift(
            <li key={`navitem-home`}>
                <NavLink to="/home" activeClassName="active">
                    <img src="/images/home.svg" className="icon" /> Home
                </NavLink>
            </li>
        );

        return jsxNavItems;
    }
}

export default withRouter(NavItems);


// WEBPACK FOOTER //
// ./src/components/navigation/NavItems.tsx